import { GhostButton, Spinner } from '@mgh-app/component-library';
import { BlindSetController } from 'components/BlindSetController';
import { CurtainSetController } from 'components/CurtainSetController';

import { MainLayoutContext } from 'layouts/MainLayout';
import { Thing, ThingIdentifier } from 'models/Thing';
import { MouseEvent, ReactNode, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { selectRoomById } from 'services/RoomService';
import { useGetChildThingsQuery, useGetThingInfoQuery } from 'services/ThingsService';
import { useAppSelector } from 'store';
import {
  backgroundColor,
  boxShadow,
  display,
  flexWrap,
  fontSize,
  gap,
  gridTemplateRows,
  justifyContent,
  margin,
  textColor,
  twCls,
} from 'style';

interface ButtonGroupProps {
  children: ReactNode;
}

const ButtonGroup = ({ children }: ButtonGroupProps): JSX.Element => {
  return (
    <div
      className={twCls(
        display('flex'),
        flexWrap('flex-wrap'),
        gap('gap-x-4', 'gap-y-1'),
        justifyContent('justify-center'),
      )}
    >
      {children}
    </div>
  );
};

const getShadesSetController = (thing: Thing | null | undefined, shadesSetId: string) => {
  switch (thing?.identifier) {
    case ThingIdentifier.Blinds:
      return <BlindSetController blindSetId={shadesSetId} />;

    case ThingIdentifier.Curtains:
      return <CurtainSetController curtainSetId={shadesSetId} />;

    default:
      throw new Error('Invalid thing identifier');
  }
};

export const Shades = (): JSX.Element => {
  const { setTitle, showNavigationButtons } = useContext(MainLayoutContext);
  const { roomId = '', parentShadesId = '' } = useParams();
  const room = useAppSelector((state) => selectRoomById(state, roomId));
  // const thing = useAppSelector((state) => selectThing(state, roomId, parentShadesId)); // TODO: thing is cleared from the store after 60 secs
  const { data: thing } = useGetThingInfoQuery({ roomId, thingId: parentShadesId });
  const { data: shades, isLoading } = useGetChildThingsQuery(parentShadesId, {
    refetchOnMountOrArgChange: true,
  });

  const [selectedShadesSet, setSelectedShadesSet] = useState<string>('');

  const handleShadesSetChange = (event: MouseEvent<HTMLButtonElement>) => {
    const newShadesSet = event.currentTarget.value;
    if (newShadesSet !== selectedShadesSet) {
      setSelectedShadesSet(newShadesSet);
    }
  };

  useEffect(() => {
    room?.nickName && setTitle(room?.nickName);
    showNavigationButtons(true);

    if (shades?.length) {
      setSelectedShadesSet(shades[0].id);
    }
  }, [room, room?.nickName, setTitle, showNavigationButtons, shades]);

  return (
    <div className={twCls(margin('mt-6'), display('grid'), gridTemplateRows('grid-rows-min-2-fc'))}>
      <h2 className={twCls(display('flex'), justifyContent('justify-center'))}>{thing?.name}</h2>
      {isLoading && <Spinner />}
      {!isLoading && shades && (
        <>
          <div className={twCls(margin('mt-4'))}>
            <ButtonGroup>
              {shades &&
                shades.map((shade, i) => (
                  <GhostButton
                    key={i}
                    value={shade.id}
                    size="small"
                    onClick={handleShadesSetChange}
                    className={twCls(
                      fontSize('text-sm'),
                      backgroundColor(
                        shade.id === selectedShadesSet ? 'bg-white' : 'bg-transparent',
                      ),
                      textColor(shade.id === selectedShadesSet ? 'text-blue-100' : 'text-grey-900'),
                      boxShadow(shade.id === selectedShadesSet ? 'shadow-lg' : 'shadow-none'),
                    )}
                  >
                    {shade.name}
                  </GhostButton>
                ))}
            </ButtonGroup>
          </div>

          <div className={twCls(margin('mt-6'), display('grid'))}>
            {selectedShadesSet && getShadesSetController(thing, selectedShadesSet)}
          </div>
        </>
      )}
    </div>
  );
};
